import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import api from '../../services/api';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the username field with whatever the user typed on the login form */
  prefillUsername?: string;
}

const ForgotPasswordDialog = ({ open, onClose, prefillUsername }: ForgotPasswordDialogProps) => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync prefill whenever the dialog opens
  useEffect(() => {
    if (open) {
      setUsername(prefillUsername ?? '');
      setSuccess(false);
      setError(null);
    }
  }, [open, prefillUsername]);

  const handleSubmit = async () => {
    if (!username.trim()) {
      setError('Please enter your username');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/api/Auth/forgot-password-alias', { username: username.trim() });
      setSuccess(true);
    } catch (err: any) {
      setError('Failed to process request. Please try again.');
      console.error('Forgot password error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forgot Password</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            If an account exists with that username, a password reset link has been sent to the
            associated email address. Please check your inbox and spam folder.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
              Enter your username and we'll send a reset link to its registered email address.
              If you don't remember your username, use <strong>Forgot Username?</strong> first.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
